import {
  FixedFeeConfig,
  Adjustment,
  ReclassBalance,
} from "@amzn/fuse-accounting-central-datastore-client";
import { ReportRow } from "./types";
import {
  toNum,
  getFirstOfMonth,
  getEarliestInvoicePeriod,
  addMonths,
  computeReclassForManualDeal,
} from "./utils";

export function computeReportRow(
  config: FixedFeeConfig,
  reportDate: string,
  reclassCutoff: string,
): ReportRow | null {
  const env = config.environment;
  const status = config.dealStatus;
  if (env !== "PRODUCTION" || status !== "ENABLED") return null;

  const dealStart = config.dealStartDate ?? "";
  const dealEnd = config.dealEndDate ?? "";
  const reportMonthStart = getFirstOfMonth(reportDate);

  // Deal must be active during report month
  if (dealEnd && dealEnd < reportMonthStart) return null;
  if (dealStart && dealStart > reportDate) return null;

  // Exclude deals pending termination where termination date has passed
  const effectiveEnd = config.effectiveTerminationDate;
  if (effectiveEnd && effectiveEnd < reportMonthStart) return null;

  const contractPrice = toNum(config.contractPrice);
  const rb: ReclassBalance | undefined = config.reclassBalance;
  const hasReclass =
    rb != null &&
    (rb.unbilledAccountsReceivables != null || rb.longTermReceivables != null);

  const invoiceBreakdown = config.invoiceBreakdown ?? {};
  const adjustments: Record<string, Adjustment> = config.adjustments ?? {};

  // Determine whether this is a multi-year deal needing a computed ST/LT split
  const isMultiYear =
    dealStart && dealEnd && addMonths(dealStart, 12) < dealEnd;

  let stIssuance: number;
  let ltIssuance: number;
  let cumulativeReclass: number;

  if (hasReclass) {
    // F3S-managed: use stored reclassBalance + adjustments
    ltIssuance =
      rb!.longTermReceivables != null ? toNum(rb!.longTermReceivables) : 0;
    stIssuance =
      rb!.unbilledAccountsReceivables != null
        ? toNum(rb!.unbilledAccountsReceivables)
        : contractPrice - ltIssuance;

    // Cumulative reclassification: sum receivableShiftAmount where adj date <= reclassCutoff
    cumulativeReclass = 0;
    for (const [adjDate, adj] of Object.entries(adjustments)) {
      if (adjDate <= reclassCutoff && adj.receivableShiftAmount != null) {
        cumulativeReclass += toNum(adj.receivableShiftAmount);
      }
    }
  } else if (isMultiYear && Object.keys(invoiceBreakdown).length > 0) {
    // Manual multi-year deal without reclassBalance: compute split from invoiceBreakdown
    const split = computeReclassForManualDeal(
      contractPrice,
      invoiceBreakdown,
      dealStart,
      reportDate,
    )!;
    stIssuance = split.stIssuance;
    ltIssuance = split.ltIssuance;
    cumulativeReclass = split.cumulativeReclass;
  } else {
    // Short-term deal, or multi-year with no invoiceBreakdown: entire contract is ST
    stIssuance = contractPrice;
    ltIssuance = 0;
    cumulativeReclass = 0;
  }

  // Invoices issued: invoice date (map key) <= report date
  let invoicesIssued = 0;
  for (const [invoiceDate, detail] of Object.entries(invoiceBreakdown)) {
    if (invoiceDate <= reportDate) {
      invoicesIssued += toNum(detail.invoiceAmount);
    }
  }

  const resellerId = config.dealConfig?.resellerIds?.[0] ?? "";

  // Automation Status: TaC configs are always automatic (pre-computed tacBreakdown).
  // STANDARD configs are automatic only if F3S has executed at least one reclassification.
  const isTac = config.fixedFeeType === "TERMINATION_AT_CONVENIENCE";
  const hasAutoReclass = Object.values(adjustments).some(
    (adj) => adj.receivableShiftAmount != null,
  );
  const hasAutoInvoice = Object.keys(invoiceBreakdown).length > 0;
  const effortType =
    isTac || (hasAutoReclass && hasAutoInvoice) ? "Automatic" : "Manual";

  const firstInvoiceBreakdownPeriod = getEarliestInvoicePeriod(
    invoiceBreakdown,
    config.invoiceFrequency,
  );

  const stEnding = stIssuance + cumulativeReclass - invoicesIssued;
  const ltEnding = ltIssuance - cumulativeReclass;

  return {
    fixedFeeId: config.fixedFeeId ?? "",
    companyCode: "",
    resellerId,
    region: "",
    dealStartDate: dealStart,
    dealEndDate: dealEnd,
    effortType,
    contractPrice,
    currencyCode: config.contractCurrencyCode ?? "",
    // FACD FixedFeeConfig will add exchangeRate field for locked-rate deals; 0 = not yet available
    fxRate: toNum((config as any).exchangeRate) || 1,
    funcCurrency: config.contractCurrencyCode ?? "",
    stIssuance,
    ltIssuance,
    cumulativeReclass,
    invoicesIssued,
    functionalInvoicesIssued: 0,
    stEnding,
    ltEnding,
    funcStIssuance: 0,
    funcStReclass: 0,
    funcStEnding: 0,
    stFxReval: 0,
    funcLtIssuance: 0,
    funcLtReclass: 0,
    funcLtEnding: 0,
    ltFxReval: 0,
    firstInvoiceBreakdownPeriod,
    serviceProvider: config.serviceProvider!,
    channelCode: "",
    invoiceStatus: "unreconciled" as const,
    trackerTotal: 0,
  };
}
